import { MenuButtonGroup } from '@/components/features/SettingMenuButtonGroup';
import { WorkspaceLayout } from '@/components/layout/WorkspaceLayout';
import { useNavState } from '@/providers/NavStateProvider';
import { useEffect } from 'react';
import { useLoading } from '@/providers/LoadingProvider';
import { useCurrentWorkspace } from '@/providers/WorkspaceProvider';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useWorkspaceUsers } from '@/hooks/api/0150_workspace_users';
import { useCreateTeam } from '@/hooks/api/0190_create_team';
import { toastSuccess } from '@/lib/toast';
import { Link, useNavigate } from 'react-router-dom';
import { useInvalidateTeams } from '@/hooks/api/0180_teams';
import { ApiErrorMessages } from '@/components/element/ApiErrorMessages';

type FormInputs = {
  identifier: string;
  name: string;
  description: string;
  admin_user_id: number;
  approver_user_id?: number;
  backoffice_user_id?: number;
  agency_enabled: boolean;
  sales_order_menu_listed: boolean;
  purchase_order_menu_listed: boolean;
};

export const WorkspaceTeamsNew = () => {
  const { setTitle, setBreadcrumbs } = useNavState();
  useEffect(() => setTitle('新規チーム登録'), []);
  useEffect(() => setBreadcrumbs([{ label: `チーム一覧`, path: `/w/${_w_!.identifier}/setting/teams` }]), []);

  const navigate = useNavigate();
  const { setShowLoading } = useLoading();
  const { _w_, me } = useCurrentWorkspace();
  const { data: workspaceUsers } = useWorkspaceUsers({ identifier: _w_!.identifier });
  const { mutate, data, isPending, error } = useCreateTeam();
  const { invalidate } = useInvalidateTeams(_w_!.identifier);

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onBlur',
    criteriaMode: 'all',
    defaultValues: {
      identifier: _w_!.identifier,
      agency_enabled: true,
      sales_order_menu_listed: true,
      purchase_order_menu_listed: true,
    },
  });

  useEffect(() => {
    if (workspaceUsers) {
      const adminUser = workspaceUsers.find((u) => u.admin);
      if (adminUser) {
        setValue('admin_user_id', adminUser.id);
      }
    }
  }, [workspaceUsers]);

  useEffect(() => setShowLoading(isPending), [isPending]);

  useEffect(() => {
    if (data) {
      toastSuccess('チームを登録しました');
      invalidate(); // Teamのキャッシュクリア
      navigate(`/w/${_w_!.identifier}/setting/teams`);
    }
  }, [data]);

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    setShowLoading(true);
    mutate(data);
  };

  if (workspaceUsers == null) return <></>;

  return (
    <WorkspaceLayout>
      <div className="container-fluid py-4">
        <MenuButtonGroup selected={'team'} />

        <div className="row">
          <div className="col-12 col-lg-9 mx-auto">
            <div className="card mb-4" id="basic-info">
              <div className="card-header d-flex align-items-center">
                <div className="col-md-8">
                  <h5 className="mb-0">新規チーム登録</h5>
                </div>
              </div>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="card-body pt-0 row">
                  <ApiErrorMessages error={error} />

                  <div className="form-group">
                    <label>
                      チーム名<span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      maxLength={100}
                      className={`form-control ${errors.name && 'is-invalid'}`}
                      placeholder={'チーム名'}
                      {...register('name', {
                        required: '入力して下さい。',
                      })}
                    />
                    {errors.name && <div className="text-xs text-danger mt-1">{errors.name.message}</div>}
                  </div>
                  <div className="form-group">
                    <label>メモ</label>
                    <textarea
                      rows={5}
                      className={`form-control ${errors.description && 'is-invalid'}`}
                      {...register('description')}
                    ></textarea>
                    {errors.description && <div className="text-xs text-danger mt-1">{errors.description.message}</div>}
                  </div>
                  <div className="col-12 col-md-6">
                    <div className="form-group">
                      <label>
                        チーム管理者<span className="text-danger">*</span>
                      </label>
                      <select className="form-control" required={true} {...register('admin_user_id')}>
                        {workspaceUsers.map((u) => (
                          <option key={`user-${u.id}`} value={u.id}>
                            {u.user_account.name}
                          </option>
                        ))}
                      </select>
                      <div className="text-xs text-muted mt-1">チームの設定変更を行える管理者です</div>
                    </div>
                  </div>
                  <div className="col-12 col-md-6">
                    <div className="form-group">
                      <label>メイン承認者</label>
                      <select className="form-control" {...register('approver_user_id')}>
                        <option></option>
                        {workspaceUsers.map((u) => (
                          <option key={`approver-${u.id}`} value={u.id}>
                            {u.user_account.name}
                          </option>
                        ))}
                      </select>
                      <div className="text-xs text-muted mt-1">
                        チーム内のタスクの承認をメインで行うメンバーを指定してください
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-md-6">
                    <div className="form-group">
                      <label>メインバックオフィス担当者</label>
                      <select className="form-control" {...register('backoffice_user_id')}>
                        <option></option>
                        {workspaceUsers.map((u) => (
                          <option key={`backoffice-${u.id}`} value={u.id}>
                            {u.user_account.name}
                          </option>
                        ))}
                      </select>
                      <div className="text-xs text-muted mt-1">
                        チーム内のタスクの事務処理担当をメインで行うメンバーを指定してください
                      </div>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>プロジェクト（売上）に表示</label>
                    <div className="form-check form-switch">
                      <input
                        type="checkbox"
                        id="on-sales-orders"
                        className="form-check-input"
                        {...register('sales_order_menu_listed')}
                      />
                      <label className="form-check-label" htmlFor="on-sales-orders">
                        メニューのプロジェクト（売上）に表示されます
                      </label>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>プロジェクト（費用）に表示</label>
                    <div className="form-check form-switch">
                      <input
                        type="checkbox"
                        id="on-purchase-orders"
                        className="form-check-input"
                        {...register('purchase_order_menu_listed')}
                      />
                      <label className="form-check-label" htmlFor="on-purchase-orders">
                        メニューのプロジェクト（費用）に表示されます
                      </label>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>代理店を有効化</label>
                    <div className="form-check form-switch">
                      <input
                        type="checkbox"
                        id="on-agency_enabled"
                        className="form-check-input"
                        {...register('agency_enabled')}
                      />
                      <label className="form-check-label" htmlFor="on-agency_enabled">
                        チームのプロジェクト（売上）で代理店フィールドが有効になります
                      </label>
                    </div>
                  </div>
                </div>
                <div className="card-footer d-flex align-items-center justify-content-end pt-0">
                  <Link to={`/w/${_w_!.identifier}/setting/teams`} className="btn btn-outline-secondary me-2">
                    戻る
                  </Link>
                  <button type="submit" className="btn bg-gradient-dark btn-icon">
                    <i className="fas fa-check-circle"></i>新規登録する
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </WorkspaceLayout>
  );
};
